import { FileSpreadsheet, ShieldCheck, UserCheck, Banknote } from "lucide-react";

const SLUG = "outsourced-financial-management";

const CHAIN = [
  {
    icon: FileSpreadsheet,
    who: "T-TITE finance team",
    what: "Prepares the payment run, with the supporting documents attached.",
  },
  {
    icon: UserCheck,
    who: "Your authorised person",
    what: "Reviews and approves the run against your own mandate.",
  },
  {
    icon: ShieldCheck,
    who: "Release",
    what: "Payment is released in line with your agreed banking controls.",
  },
];

/**
 * Supplier payments, called out on their own.
 *
 * Two reasons this is a section and not a tick in a list: it changes the
 * workload materially, so it changes the fee; and it carries governance
 * weight, so the control chain is shown rather than implied. We prepare and
 * support - your mandate still releases the money.
 */
export const PaymentManagement = ({ onScope }) => (
  <section
    data-testid={`${SLUG}-payments`}
    className="border-y border-border bg-[color:var(--tt-teal-mist)] py-14 sm:py-20"
  >
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-primary shadow-sm">
            <Banknote className="h-5 w-5" aria-hidden="true" />
          </span>
          <h2 className="mt-5 text-[27px] font-bold leading-[1.15] tracking-[-0.015em] text-foreground sm:text-[32px]">
            Need us to manage supplier payments too?
          </h2>
          <p className="mt-4 max-w-[50ch] text-[15px] leading-relaxed text-[color:var(--tt-teal-deep)]">
            We can fold supplier payment administration into your finance function — managing
            payment schedules, preparing payment batches and keeping the supporting
            documentation in order.
          </p>
          <button
            type="button"
            onClick={onScope}
            data-testid={`${SLUG}-payments-scope`}
            className="tt-link mt-5 text-sm font-semibold"
          >
            Include payments in my scope
          </button>
        </div>

        <div className="lg:col-span-7">
          <ol className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {CHAIN.map(({ icon: Icon, who, what }, index) => (
              <li
                key={who}
                data-testid={`${SLUG}-payment-step-${index + 1}`}
                className="tt-card flex flex-col p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[color:var(--tt-accent)]/10 text-[color:var(--tt-accent)]">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="font-amount text-[12px] font-semibold text-muted-foreground/70">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-4 text-[13px] font-bold uppercase tracking-[0.08em] text-foreground">
                  {who}
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{what}</p>
              </li>
            ))}
          </ol>

          <p className="mt-5 rounded-xl border border-[color:var(--tt-teal-line)] bg-white p-5 text-[13px] leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">On controls:</span> payment approval
            and release arrangements are agreed during onboarding and structured around
            appropriate authorisation and segregation-of-duties controls.
          </p>
        </div>
      </div>
    </div>
  </section>
);
